import { Component, OnInit, PLATFORM_ID, inject } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup } from '@angular/forms';
import { take } from 'rxjs/operators';

import { AuthLayoutComponent } from '../../shared/layouts/auth-layout.component';
import { ConfirmModalComponent } from '../../shared/components/confirm-modal/confirm-modal.component';
import { LoadingComponent } from '../../shared/components/loading/loading.component';
import { I18nPipe } from '../../shared/pipes/i18n.pipe';
import { ToastService } from '../../shared/services/toast.service';
import { AuthService } from '../../core/services/auth.service';
import { CartFacade } from '../../core/store/cart/cart.facade';
import { HeaderService } from '../../shared/services/header.service';
import { UserFacade } from '../../core/store/user/user.facade';
import { UserService } from '../../core/services/user.service';
import { UtmPreservationService } from '../../core/services/utm-preservation.service';
import { AvailableOffersFacade } from '../../core/store/available-offers/available-offers.facade';
import {
  ExternalChannelAccessResponse,
  FixaResource,
  SelectContractRequest,
} from '../../core/models/login.model';
import {
  formatFixaContractDisplay,
  formatFixaContractDisplayFromList,
} from '../../core/utils/fixa-contract-display.util';
import { constructFinalRedirectWithToken, needsAddressCompletion, STORAGE_EXTERNAL_CHANNEL_ACCESS } from '../../core/utils/external-channel-access.util';
import { applySelectContractToSession } from '../../core/utils/select-contract-response.util';
import { unformatCnpj } from '../../core/utils/cnpj.util';
import { MceRedirectProductService } from '../../core/services/mce-redirect-product.service';
import { ExternalChannelParentMessageService } from '../../core/services/external-channel-parent-message.service';
import { environment } from '../../../environments/environment';

const SELECTED_CNPJ_KEY = 'umkt_selected_cnpj';
const CLARO_EMPRESAS_HOME_URL = `${environment.mceRedirectUrl}/#/HomeLogada`;

const ACTIVE_FIXA_STATUSES = ['ACTIVE', 'CONECTADO'];

type SegmentItem =
  | { kind: 'MOBILE'; document: string; companyName?: string }
  | { kind: 'FIXA'; contract: FixaResource };

@Component({
  selector: 'umkt-segment-selection',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    RouterModule,
    MatIconModule,
    AuthLayoutComponent,
    ConfirmModalComponent,
    LoadingComponent,
    I18nPipe,
  ],
  templateUrl: './segment-selection.component.html',
  styleUrls: [
    '../../../assets/style/login-register.component.scss',
    './segment-selection.component.scss',
  ],
})
export class SegmentSelectionComponent implements OnInit {
  searchForm!: FormGroup;
  searchTerm = '';

  loading = true;
  actionLoading = false;
  showLogoutConfirm = false;

  mobileItems: SegmentItem[] = [];
  fixaItems: SegmentItem[] = [];

  filteredMobileItems: SegmentItem[] = [];
  filteredFixaItems: SegmentItem[] = [];

  get isBusy(): boolean {
    return this.loading || this.actionLoading;
  }
  private fb = inject(FormBuilder);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private toastService = inject(ToastService);
  private authService = inject(AuthService);
  private cartFacade = inject(CartFacade);
  private headerService = inject(HeaderService);
  private userFacade = inject(UserFacade);
  private userService = inject(UserService);
  private availableOffersFacade = inject(AvailableOffersFacade);
  private utmPreservationService = inject(UtmPreservationService);
  private mceRedirectProductService = inject(MceRedirectProductService);
  private externalChannelParentMessageService = inject(ExternalChannelParentMessageService);
  private platformId = inject(PLATFORM_ID);


  ngOnInit(): void {
    this.cartFacade.clearExternalChannelCart();
    this.searchForm = this.fb.group({ search: [''] });
    this.loadDataFromSession();
  }

  private loadDataFromSession(): void {
    if (!isPlatformBrowser(this.platformId)) {
      this.loading = false;
      return;
    }

    const raw = sessionStorage.getItem(STORAGE_EXTERNAL_CHANNEL_ACCESS);
    if (!raw) {
      this.loading = false;
      this.router.navigate(['/']);
      return;
    }

    let parsed: ExternalChannelAccessResponse | null = null;
    try {
      parsed = JSON.parse(raw) as ExternalChannelAccessResponse;
    } catch {
      parsed = null;
    }

    if (!parsed) {
      this.loading = false;
      this.router.navigate(['/']);
      return;
    }

    if (needsAddressCompletion(parsed.completeAddress)) {
      this.loading = false;
      this.router.navigate(['/external-channel-complete-address']);
      return;
    }

    if (parsed.userRequest?.document) {
      this.mobileItems = [
        {
          kind: 'MOBILE',
          document: parsed.userRequest.document,
          companyName: parsed.userRequest.business,
        },
      ];
    }

    const contracts = Array.isArray(parsed.contracts) ? parsed.contracts : [];
    const activeContracts = contracts.filter(contract =>
      ACTIVE_FIXA_STATUSES.includes(contract.status?.toUpperCase?.() ?? '')
    );

    this.fixaItems = activeContracts.map(
      c => ({ kind: 'FIXA', contract: c } as SegmentItem)
    );

    this.filteredMobileItems = [...this.mobileItems];
    this.filteredFixaItems = [...this.fixaItems];

    this.loading = false;
  }

  clearSearch(): void {
    this.searchTerm = '';
    this.filteredMobileItems = [...this.mobileItems];
    this.filteredFixaItems = [...this.fixaItems];
  }

  onSearchChange(event: Event): void {
    const value = (event.target as HTMLInputElement).value ?? '';
    this.searchTerm = value;
    this.filterItems(value);
  }

  private filterItems(searchTerm: string): void {
    const term = (searchTerm || '').toLowerCase().trim();
    const termClean = unformatCnpj(term).toLowerCase();
    if (!term) {
      this.filteredMobileItems = [...this.mobileItems];
      this.filteredFixaItems = [...this.fixaItems];
      return;
    }

    this.filteredMobileItems = this.mobileItems.filter((item) => {
      if (item.kind !== 'MOBILE') return false;
      const doc = item.document?.toLowerCase?.() ?? '';
      const company = item.companyName?.toLowerCase?.() ?? '';
      const docClean = unformatCnpj(doc).toLowerCase();
      return (
        doc.includes(term) ||
        company.includes(term) ||
        (termClean.length > 0 && docClean.includes(termClean))
      );
    });

    this.filteredFixaItems = this.fixaItems.filter((item) => {
      if (item.kind !== 'FIXA') return false;
      const c = item.contract;
      const haystack = [
        formatFixaContractDisplay(c),
        c.customerAccountId,
        String(c.operatorCode ?? ''),
        c.status,
        c.sourceSystemDescription,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(term);
    });
  }

  selectMobile(item: SegmentItem): void {
    if (item.kind !== 'MOBILE' || this.actionLoading) return;

    const clean = unformatCnpj(item.document || '');
    if (!clean) {
      return;
    }

    this.actionLoading = true;
    this.userFacade.selectCnpj(clean);
    this.availableOffersFacade.loadAvailableOffers(clean);
    this.navigateAfterSegmentSelection(() => {
      this.actionLoading = false;
    });
  }

  selectFixa(item: SegmentItem): void {
    if (item.kind !== 'FIXA' || this.actionLoading) return;

    this.actionLoading = true;
    this.userFacade.setSelectedContract(item.contract);

    if (isPlatformBrowser(this.platformId)) {
      try {
        localStorage.setItem(SELECTED_CNPJ_KEY, item.contract.customerAccountId);
      } catch {
        // ignore
      }
    }

    this.userFacade.getLoginData().pipe(take(1)).subscribe(loginData => {
      if (!loginData) {
        this.actionLoading = false;
        this.toastService.showError(
          'Erro ao obter dados da sessão. Por favor, faça login novamente.',
          5000,
          'top-center'
        );
        this.router.navigate(['/login']);
        return;
      }

      const request: SelectContractRequest = {
        operatorCode: item.contract.operatorCode,
        customerAccountId: item.contract.customerAccountId,
        document: loginData.document || '',
        sessionId: loginData.sessionId || '',
      };

      this.userService.selectContract(request).pipe(take(1)).subscribe({
        next: response => {
          if (response) {
            applySelectContractToSession(response, this.authService, this.userFacade);
            this.availableOffersFacade.clearAvailableOffers();
          }
          this.navigateAfterSegmentSelection(() => {
            this.actionLoading = false;
          });
        },
        error: () => {
          this.actionLoading = false;
          this.toastService.showError(
            'Erro ao selecionar contrato. Tente novamente.',
            5000,
            'top-center'
          );
        },
      });
    });
  }

  fixaContractLabel(customerAccountId: string): string {
    const contracts = this.fixaItems
      .filter((item): item is { kind: 'FIXA'; contract: FixaResource } => item.kind === 'FIXA')
      .map(item => item.contract);

    return formatFixaContractDisplayFromList(contracts, customerAccountId);
  }

  private navigateAfterSegmentSelection(onComplete?: () => void): void {
    const queryParams = this.utmPreservationService.addPreservedUtmToQueryParams({
      ...(this.route.snapshot?.queryParams || {}),
    });

    const finish = (path: string | string[]): void => {
      this.router.navigate(Array.isArray(path) ? path : [path], { queryParams });
      onComplete?.();
    };

    this.mceRedirectProductService
      .tryAddFromStoredIntent()
      .pipe(take(1))
      .subscribe(result => {
        if (result.handled) {
          finish(result.success ? '/checkout' : '/');
          return;
        }

        const redirect = constructFinalRedirectWithToken();
        if (redirect) {
          this.router.navigate(redirect.commands, {
            queryParams: { ...queryParams, ...redirect.queryParams },
          });
          onComplete?.();
          return;
        }

        finish('/');
      });
  }

  openLogoutConfirm(): void {
    this.showLogoutConfirm = true;
  }

  handleLogoutConfirm(): void {
    this.showLogoutConfirm = false;
    this.logout();
  }

  handleLogoutCancel(): void {
    this.showLogoutConfirm = false;
  }

  private logout(): void {
    this.authService.logout();
    this.cartFacade.clearCart();
    this.headerService.resetActiveTopNavItem();
    this.toastService.showSuccess('Sessão encerrada com sucesso.', 3000, 'top-center');
    this.router.navigate(['/login']);
  }

  goBack(): void {
    if (isPlatformBrowser(this.platformId)) {
      if (this.externalChannelParentMessageService.notifyHubClosed()) {
        return;
      }

      window.location.href = CLARO_EMPRESAS_HOME_URL;
      return;
    }

    this.router.navigate(['/']);
  }

  trackByItem(index: number, item: SegmentItem): string {
    if (item.kind === 'MOBILE') return `MOBILE:${item.document}`;
    return `FIXA:${item.contract.customerAccountId}:${item.contract.operatorCode}`;
  }
}
